// Tabbed side panel: Chat | People | Files
// Updated for redesigned ChatPanel and ParticipantsPanel

import { memo, useState, useEffect, useMemo, Suspense } from "react"
import { lazyWithReload } from "@/shared/utils/lazy-with-reload"
import type { ChatMessage, ChatType, Participant, ParticipantRole, SharedFile, MeetingInfo } from "@vc/models"
import type { ChatAttachmentKind } from "@/features/chat"
import type { PermissionRequest } from "@vc/views/vcLayout/components/overlays/JoinRequestsWidget"
import { withoutShareOnly, type BlockedShare } from "@/features/meeting/services/share-only-roster"
import { FilesPanel } from "./FilesPanel"
import { X } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

const ChatPanel = lazyWithReload(() => import("@/features/chat").then(m => ({ default: m.ChatPanel })))
const ParticipantsPanel = lazyWithReload(() => import("./ParticipantsPanel").then(m => ({ default: m.ParticipantsPanel })))
const InfoPanel = lazyWithReload(() => import("./InfoPanel").then(m => ({ default: m.InfoPanel })))

/**
 * The chunks the three lazy tabs above split into.
 *
 * A lazy component suspends the FIRST time it renders, which is why opening a
 * tab was rough exactly once and smooth from then on — the second click has the
 * chunk in memory. Warming them while the browser is idle moves that one fetch
 * off the click, so the first open behaves like every later one.
 */
const PANEL_CHUNKS: Array<() => Promise<unknown>> = [
  () => import("@/features/chat"),
  () => import("./ParticipantsPanel"),
  () => import("./InfoPanel"),
]

function usePrefetchPanels() {
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number
      cancelIdleCallback?: (id: number) => void
    }
    // Failures are ignored on purpose: this only warms the cache, and the real
    // import on click keeps `lazyWithReload`'s stale-chunk recovery.
    const warm = () => {
      for (const load of PANEL_CHUNKS) void load().catch(() => {})
    }
    const id = w.requestIdleCallback ? w.requestIdleCallback(warm) : window.setTimeout(warm, 1200)
    return () => {
      if (w.cancelIdleCallback) w.cancelIdleCallback(id)
      else window.clearTimeout(id)
    }
  }, [])
}

const PanelSkeleton = () => (
  <div className="flex-1 flex flex-col gap-3 p-4 animate-pulse">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="h-10 rounded-lg bg-vc-panel-input-bg opacity-60" />
    ))}
  </div>
)

type Tab = "chat" | "people" | "files" | "info"

export const SidePanelTabs = memo(function SidePanelTabs({
  defaultTab = "chat",
  participants,
  localParticipant,
  messages,
  privateThreads,
  unreadGroup,
  unreadPrivate,
  files,
  lobbyUsers = [],
  canChat,
  canShare,
  meetingInfo,
  typingUsers,
  onMuteAll,
  onStopAllVideos,
  onStopAllScreens,
  onEndMeeting,
  onClose,
  onSendMessage,
  onAttachChat,
  panelVisible = true,
  onActiveChatThreadChange,
  onClearChatThread,
  onMarkChatRead,
  onAdmit,
  onReject,
  onAdmitAll,
  onShareFile,
  onMarkFileRead,
  onClearFiles,
  onMuteParticipant,
  onUnmuteParticipant,
  onStopVideo,
  onStartVideo,
  onStopScreen,
  onRemoveParticipant,
  onChangeRole,
  onRequestAudio,
  onRequestVideo,
  onSpotlight,
  onTabChange,
  hideHeader = false,
  onShare,
  onChat,
  onSelectParticipant,
  selectedPrivateChat,
  onShowToast,
  canPrivateChat,
  canSendFiles,
  canUseEmojis,
  shareBlocked,
  onAllowShare,
  onSetPresenter,
  onSetAnnotator,
  permissionRequests,
  onApproveRequest,
  onRejectRequest,
}: {
  defaultTab?: Tab
  participants: Participant[]
  localParticipant: Participant
  messages: ChatMessage[]
  /** peerId → private conversation, held in the chat store (see ChatPanel). */
  privateThreads?: Record<string, ChatMessage[]>
  /** Group-thread unread, for the chat's Group tab badge. */
  unreadGroup?: number
  /** peerId → unread count, for the private-list badges. */
  unreadPrivate?: Record<string, number>
  unreadChat: number
  files: SharedFile[]
  /** `shareOnly`: a Screen Share page knocking, with the account email it came from. */
  lobbyUsers?: { id: string; name: string; shareOnly?: boolean; email?: string | null }[]
  canChat?: boolean
  /** Host-controls gate forwarded to ChatPanel. */
  canPrivateChat?: boolean
  /** Host-controls gate forwarded to ChatPanel. */
  canSendFiles?: boolean
  /** Host-controls gate forwarded to ChatPanel. */
  canUseEmojis?: boolean
  canShare?: boolean
  meetingInfo?: MeetingInfo
  typingUsers?: string[]
  onMuteAll?: () => void
  onStopAllVideos?: () => void
  onStopAllScreens?: () => void
  onEndMeeting?: () => void
  onClose: () => void
  onSendMessage: (text: string, type: ChatType, to?: string | null) => void
  /** Upload + send a file. Absent (guests) hides the attach button. */
  onAttachChat?: (file: File, kind: ChatAttachmentKind, to?: string | null) => Promise<void> | void
  /**
   * Whether the side panel is actually on screen. The desktop layout keeps this
   * component mounted and animates the container to zero width, so "mounted"
   * says nothing about whether the user can see it — and chat needs to know,
   * or a closed panel keeps marking arriving messages as read.
   */
  panelVisible?: boolean
  /** Which conversation is on screen — drives per-thread unread. */
  onActiveChatThreadChange?: (thread: string | null) => void
  onClearChatThread?: (thread: string) => void
  onMarkChatRead: () => void
  onAdmit?: (id: string) => void
  onReject?: (id: string) => void
  onAdmitAll?: () => void
  onShareFile?: (file: File) => void
  onMarkFileRead?: (id: string) => void
  onClearFiles?: () => void
  onMuteParticipant?: (id: string) => void
  onUnmuteParticipant?: (id: string) => void
  onStopVideo?: (id: string) => void
  onStartVideo?: (id: string) => void
  onStopScreen?: (id: string) => void
  onRemoveParticipant?: (id: string) => void
  onChangeRole?: (id: string, role: ParticipantRole) => void
  onRequestAudio?: (id: string) => void
  onRequestVideo?: (id: string) => void
  onSpotlight?: (id: string) => void
  onPin?: (id: string) => void
  onTabChange?: (tab: Tab) => void
  hideHeader?: boolean
  onShare?: (url: string) => void
  onChat?: (id: string) => void
  onSelectParticipant?: (id: string) => void
  selectedPrivateChat?: string | null
  onShowToast?: (message: string, senderName: string) => void
  onClearChat?: () => void
  canClear?: boolean
  /** Screen-share pages a moderator removed, for "Allow back" (People tab). */
  shareBlocked?: BlockedShare[]
  onAllowShare?: (id: string) => void
  /** Presenter on/off for a screen-share session's row. */
  onSetPresenter?: (id: string, granted: boolean) => void
  /** Annotation access on/off for a participant. */
  onSetAnnotator?: (id: string, granted: boolean) => void
  permissionRequests?: PermissionRequest[]
  onApproveRequest?: (request: PermissionRequest) => void
  onRejectRequest?: (request: PermissionRequest) => void
}) {
  const t = useTranslation();
  const [peopleView, setPeopleView] = useState<"main" | "invite">("main")
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab)
  // Who private chat can be addressed to: people, not screen-share sessions,
  // which take no messages (the server sends them no chat at all).
  const chatParticipants = useMemo(() => withoutShareOnly(participants), [participants])

  usePrefetchPanels()

  // Update activeTab when defaultTab prop changes
  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  // On desktop this panel is never unmounted — MeetingScreen slides it off with
  // translateX(100%) — so `peopleView` outlives every close. Opening "+" and
  // then dismissing the panel left the view latched on "invite", and the next
  // click on Participants reopened the capacity form instead of the list, with
  // no obvious way back to it. Closing the panel, or moving to another tab,
  // returns People to its list.
  useEffect(() => {
    if (!panelVisible || activeTab !== "people") setPeopleView("main")
  }, [panelVisible, activeTab])

  const handleNavigateToInfo = () => {
    setActiveTab('info')
    onTabChange?.('info')
  }

  return (
    <div className="flex h-full flex-col bg-pannelcolor rounded-l-xl overflow-hidden shadow-lg text-vc-panel-text">
      {/* Header - Title and Close button for desktop */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-vc-panel-border bg-card">
          <h2 className="text-base font-semibold text-vc-panel-text">
            {activeTab === "chat" ? t("panels.chat") : activeTab === "people" ? t("panels.participants") : activeTab === "files" ? t("panels.files") : t("panels.meetingInfo")}
          </h2>
          <button
            onClick={onClose}
            className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-vc-panel-text-muted hover:bg-vc-panel-hover transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      {/* <div className="flex items-center gap-1 border-b border-slate-200 dark:border-border px-3 py-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
              activeTab === tab.id
                ? "border-2 border-slate-300 dark:border-border bg-card text-slate-800 dark:text-foreground shadow-sm"
                : "border-2 border-transparent text-slate-500 dark:text-muted-foreground hover:bg-slate-100 dark:hover:bg-muted hover:text-slate-700 dark:hover:text-foreground"
            )}
          >
            <tab.icon className={cn("h-4 w-4", tab.color)} />
            <span>{tab.label}</span>
            {tab.count > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sky-100 px-1.5 text-xs font-semibold text-sky-700">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div> */}

      {/* Direct content */}
      <div className="min-h-0 flex-1 flex w-full">
        {activeTab === "chat" && (
          <Suspense fallback={<PanelSkeleton />}>
            <ChatPanel
              messages={messages}
              privateThreads={privateThreads}
              unreadGroup={unreadGroup}
              unreadPrivate={unreadPrivate}
              // The SFU peerId is what the room channel addresses; the roster
              // `id` is only a fallback for preview renders with no session.
              localUserId={localParticipant.sfuPeerId || localParticipant.id}
              canChat={canChat}
              canPrivateChat={canPrivateChat}
              canSendFiles={canSendFiles && !!onAttachChat}
              canUseEmojis={canUseEmojis}
              onSend={onSendMessage}
              onAttach={onAttachChat}
              visible={panelVisible}
              onActiveThreadChange={onActiveChatThreadChange}
              onClearThread={onClearChatThread}
              onMarkRead={onMarkChatRead}
              typingUsers={typingUsers}
              participants={chatParticipants}
              initialSelectedPrivateChat={selectedPrivateChat}
              onShowToast={onShowToast}
            />
          </Suspense>
        )}
        {activeTab === "people" && (
          <Suspense fallback={<PanelSkeleton />}>
            <ParticipantsPanel
              participants={participants}
              localParticipant={localParticipant}
              lobbyUsers={lobbyUsers}
              view={peopleView}
              onViewChange={setPeopleView}
              onAdmit={onAdmit}
              onReject={onReject}
              onAdmitAll={onAdmitAll}
              onMuteAll={onMuteAll}
              onStopAllVideos={onStopAllVideos}
              onStopAllScreens={onStopAllScreens}
              onEndMeeting={onEndMeeting}
              onMuteParticipant={onMuteParticipant}
              onUnmuteParticipant={onUnmuteParticipant}
              onStopVideo={onStopVideo}
              onStartVideo={onStartVideo}
              onStopScreen={onStopScreen}
              onRemoveParticipant={onRemoveParticipant}
              onChangeRole={onChangeRole}
            onRequestAudio={onRequestAudio}
            onRequestVideo={onRequestVideo}
              onSpotlight={onSpotlight}
              onChat={onChat}
              onSelectParticipant={onSelectParticipant}
              onShowMeetingInfo={handleNavigateToInfo}
              shareBlocked={shareBlocked}
              onAllowShare={onAllowShare}
              onSetPresenter={onSetPresenter}
              onSetAnnotator={onSetAnnotator}
              permissionRequests={permissionRequests}
              onApproveRequest={onApproveRequest}
              onRejectRequest={onRejectRequest}
            />
          </Suspense>
        )}
        {activeTab === "files" && (
          <div className="flex-1 min-h-0 bg-pannelcolor text-vc-panel-text">
            <FilesPanel
              files={files}
              localUserId={localParticipant.id}
              canShare={canShare}
              onShare={onShareFile}
              onMarkRead={onMarkFileRead}
              onClear={onClearFiles}
            />
          </div>
        )}
        {activeTab === "info" && meetingInfo && (
          <Suspense fallback={<PanelSkeleton />}>
            <InfoPanel meetingInfo={meetingInfo} onShare={onShare} />
          </Suspense>
        )}
      </div>
    </div>
  )
})
